import { Loader2, Sparkles } from "lucide-react";
import { useState } from "react";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { invokeAi } from "./invokeAi";

/**
 * Summarize a stored call transcript on demand. The transcript is read with
 * the caller's JWT (a call this user cannot open cannot be summarized), and
 * the result renders inline, labelled with its provider so sandbox output
 * never passes as a model.
 */
export const CallSummaryButton = ({ callId }: { callId: string }) => {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<{
    text: string;
    provider: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const summarize = async () => {
    setBusy(true);
    setError(null);
    const { data, error: fnError } = await invokeAi<{
      text: string;
      provider: string;
    }>({
      action: "call_summary",
      call_id: callId,
      workspace_id: getActiveWorkspaceId(),
    });
    setBusy(false);
    if (fnError || !data) {
      setError(fnError ?? "Summary unavailable");
      return;
    }
    setSummary(data);
  };

  if (summary) {
    return (
      <div className="mt-1 rounded-md border border-border-subtle bg-muted/30 p-2">
        <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-foreground">
          {summary.text}
        </p>
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {summary.provider === "sandbox"
            ? "sandbox (no model called)"
            : `via ${summary.provider}`}
        </span>
      </div>
    );
  }

  return (
    <span className="mt-0.5 flex items-center gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={summarize}
        className="flex items-center gap-1 text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
      >
        {busy ? (
          <Loader2 className="size-3 animate-spin" />
        ) : (
          <Sparkles className="size-3" />
        )}
        Summarize call
      </button>
      {error ? (
        <span className="text-[11.5px] text-destructive">{error}</span>
      ) : null}
    </span>
  );
};
